import { paynowCode } from "@ohc/shared";
import { useEffect, useState } from "react";
import { ApiError, type SaleCost, api, formatMoney } from "../api";
import { SelectField } from "../fields";
import { Alert, Badge, Empty, Modal, Offcanvas, PanelSection, TwoLine, Working } from "../ui";
import {
  type Catalog,
  dayBack,
  KEPT_DAYS,
  type LocalBuyback,
  type LocalSale,
  payLater,
  photosWaiting,
  flagSale,
  recentSales,
  type SaleFlagState,
  todaysBuybacks,
  tillAccount,
  todaysSales,
  salesDaysBack,
  useQueue,
  voidSale,
  photosOnDevice,
} from "./engine";
import { PosStaff } from "./PosStaff";

/**
 * Today on this iPad, in the template's right-hand panel: every sale with its reference,
 * who sold it and whether it is paid, so the booth can answer "did that one go in?"
 * without asking anyone. An unpaid sale is paid from here when the money comes; one that
 * should not have been made is voided, by a supervisor or with a supervisor's PIN.
 */
export function PosSales({
  catalog,
  seller,
  setup,
  onSetup,
  canVoid,
  canSetAccounts,
  seesProfit = false,
  sellOnly = false,
  onReload,
  onCorrect,
  onClose,
}: {
  /** A role that sees costs: each sale's cost and profit, from the desk (D, 9 October 2026). */
  readonly seesProfit?: boolean;
  /** Sales staff: only where this iPad is selling, no takings and no list of sales (D, 1 November 2026). */
  readonly sellOnly?: boolean;
  readonly catalog: Catalog;
  readonly seller: { id: string; name: string };
  readonly setup: { locationId: string; event: string };
  readonly onSetup: (setup: { locationId: string; event: string }) => void;
  readonly canVoid: boolean;
  readonly canSetAccounts: boolean;
  /** Fetch the catalog again, after a PIN is set or the till's accounts are changed. */
  readonly onReload: () => Promise<Catalog | undefined>;
  /** Put a sale right: it goes back into the basket, and whoever allowed it is named. */
  readonly onCorrect: (sale: LocalSale, by: { id: string; name: string }) => void;
  readonly onClose: () => void;
}): React.ReactElement {
  const [sales, setSales] = useState<LocalSale[]>([]);
  const [buybacks, setBuybacks] = useState<LocalBuyback[]>([]);
  const [photosLeft, setPhotosLeft] = useState(0);
  const [open, setOpen] = useState<string | undefined>();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [approving, setApproving] = useState<LocalSale | undefined>();
  /** A sale somebody wants to put right, waiting for a supervisor's PIN. */
  const [correcting, setCorrecting] = useState<LocalSale | undefined>();
  /** Sales of earlier days a supervisor has since allowed to be put right. */
  const [earlier, setEarlier] = useState<LocalSale[]>([]);
  /**
   * Which day's sales are listed, as days back from today (D, 4 October: a sale keyed wrong
   * was only noticed the next day; 5 October: and 3 October could not be chosen at all). Any
   * day still on the iPad can be picked, and the figures at the top are that day's.
   */
  const [back, setBack] = useState(0);
  const [listed, setListed] = useState<LocalSale[]>([]);
  const queue = useQueue();

  // What the desk has said about this iPad's sales comes with the stock list, so opening
  // Today fetches it again. With no signal the last copy stands.
  useEffect(() => {
    void onReload().catch(() => undefined);
  }, []);

  const flagOf = (saleId: string): SaleFlagState | undefined => catalog.flags?.find((flag) => flag.saleId === saleId);

  useEffect(() => {
    void todaysSales().then(async (today) => {
      setSales(today);
      const shown = new Set(today.map((sale) => sale.id));
      const allowed = new Set((catalog.flags ?? []).filter((flag) => flag.status === "allowed").map((flag) => flag.saleId));
      setEarlier(
        (await recentSales()).filter(
          (sale) => !shown.has(sale.id) && allowed.has(sale.id) && (sale.status === "paid" || sale.status === "unpaid"),
        ),
      );
    });
    void todaysBuybacks().then(setBuybacks);
    void photosWaiting().then(setPhotosLeft);
  }, [queue.revision, catalog]);

  useEffect(() => {
    if (back === 0) {
      setListed(sales);
      return;
    }
    void salesDaysBack(back).then(setListed);
  }, [back, sales]);

  const paid = listed.filter((sale) => sale.status === "paid");
  /** What each listed sale cost, asked of the desk; nothing when offline or not allowed. */
  const [costs, setCosts] = useState<Map<string, SaleCost>>(new Map());
  useEffect(() => {
    if (!seesProfit || listed.length === 0) return;
    void api
      .saleCosts(listed.map((sale) => sale.id))
      .then((result) => setCosts(new Map(result.costs.map((cost) => [cost.saleId, cost]))))
      .catch(() => undefined);
  }, [seesProfit, listed]);
  const costed = paid.filter((sale) => costs.has(sale.id));
  const profit = costed.reduce((sum, sale) => sum + sale.totalCents - (costs.get(sale.id)?.costCents ?? 0), 0);
  const dayName = (daysBack: number): string =>
    new Date(`${dayBack(daysBack)}T12:00:00+08:00`).toLocaleDateString("en-SG", { weekday: "short", day: "numeric", month: "short", timeZone: "Asia/Singapore" });
  const cash = paid.filter((sale) => sale.method === "cash").reduce((sum, sale) => sum + sale.totalCents, 0);
  const paynow = paid.filter((sale) => sale.method === "paynow").reduce((sum, sale) => sum + sale.totalCents, 0);

  async function act(work: () => Promise<void>): Promise<void> {
    setBusy(true);
    try {
      await work();
      setOpen(undefined);
      setReason("");
      setSales(await todaysSales());
      setEarlier((await recentSales()).filter((sale) => earlier.some((shown) => shown.id === sale.id)));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Offcanvas
        wide
        title={`${sellOnly ? "Sales session" : "Today's sales"} - ${catalog.device.name} (${catalog.device.code})`}
        onClose={onClose}
      >
        {sellOnly ? null : (
        <PanelSection>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-[#8c9097] dark:text-white/50 text-[0.75rem] mb-1">Cash received</p>
              <p className="font-semibold text-[1.25rem] mb-0">{formatMoney(cash)}</p>
            </div>
            <div>
              <p className="text-[#8c9097] dark:text-white/50 text-[0.75rem] mb-1">PayNow received</p>
              <p className="font-semibold text-[1.25rem] mb-0">{formatMoney(paynow)}</p>
            </div>
            <div>
              <p className="text-[#8c9097] dark:text-white/50 text-[0.75rem] mb-1">Number of sales</p>
              <p className="font-semibold text-[1.25rem] mb-0">{paid.length}</p>
            </div>
          </div>
          {seesProfit && costed.length > 0 ? (
            <p className={`text-center text-[0.875rem] font-semibold mt-3 mb-0 ${profit < 0 ? "text-danger" : "text-success"}`}>
              Profit {formatMoney(profit)} on {formatMoney(costed.reduce((sum, sale) => sum + sale.totalCents, 0))} sold
              {costed.length < paid.length ? ` (${paid.length - costed.length} not yet on the desk)` : ""}
            </p>
          ) : null}
          {back > 0 ? (
            <p className="text-center text-[0.75rem] text-warning mt-2 mb-0">Totals for {dayName(back)}, not today.</p>
          ) : null}
        </PanelSection>
        )}

        <PanelSection title="Sales session" last={sellOnly}>
          <div className="grid grid-cols-12 gap-4">
            <div className="sm:col-span-6 col-span-12">
              <label htmlFor="pos-place" className="form-label">
                Sales location
              </label>
              <SelectField
                id="pos-place"
                value={setup.locationId}
                placeholder="Select sales location"
                options={catalog.locations.map((place) => ({ value: place.id, label: place.name }))}
                onChange={(locationId) => onSetup({ ...setup, locationId })}
              />
            </div>
            <div className="sm:col-span-6 col-span-12">
              <label htmlFor="pos-event" className="form-label">
                Event or trading day
              </label>
              {catalog.locations.find((place) => place.id === setup.locationId)?.kind === "shop" ? (
                <div id="pos-event" className="form-control bg-light dark:bg-bodybg">
                  {new Intl.DateTimeFormat("en-SG", { weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Singapore" }).format(new Date())}
                  <span className="text-[#8c9097] dark:text-white/50">, updates automatically</span>
                </div>
              ) : (
                <input
                  id="pos-event"
                  type="text"
                  className="form-control"
                  value={setup.event}
                  placeholder="Suntec Trade Show - Day 1"
                  onChange={(event) => onSetup({ ...setup, event: event.target.value })}
                />
              )}
            </div>
          </div>
          <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-2 mb-0">
            {catalog.locations.length === 0
              ? "No sales locations have been set up yet. A system administrator adds them on the dashboard, under Inventory, Add location, and they appear here."
              : "Both are included in every sale from this iPad, with its date and time, and used in location and event reports. They remain on this iPad until changed."}
          </p>
        </PanelSection>

        {sellOnly ? null : (
        <>
        {earlier.length > 0 ? (
          <PanelSection title="Approved corrections">
            <ul className="list-none mb-0">
              {earlier.map((sale) => (
                <li key={sale.id} className="py-3 border-b border-defaultborder">
                  <div className="flex items-center justify-between gap-3">
                    <TwoLine
                      main={sale.reference}
                      sub={`${new Date(sale.soldAt).toLocaleString("en-SG", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })} · ${sale.soldByName}`}
                    />
                    <div className="font-semibold">{formatMoney(sale.totalCents)}</div>
                  </div>
                  <PutRight
                    sale={sale}
                    flag={flagOf(sale.id)}
                    canVoid={canVoid}
                    busy={busy}
                    onCorrect={onCorrect}
                    onAskPin={() => setCorrecting(sale)}
                    onAsk={(note) => void act(() => flagSale(sale, note, seller.id))}
                  />
                </li>
              ))}
            </ul>
          </PanelSection>
        ) : null}

        <PanelSection title="Sales" last={!canSetAccounts}>
          {/* Earlier days too, for a sale keyed wrong found later: put right the same way as today's. */}
          <div className="flex flex-wrap items-center gap-2 mb-3" role="group" aria-label="Which day">
            {[
              { back: 0, label: "Today" },
              { back: 1, label: "Yesterday" },
            ].map((choice) => (
              <button
                key={choice.back}
                type="button"
                aria-pressed={back === choice.back}
                className={`ti-btn !mb-0 !py-1 !px-3 ${back === choice.back ? "ti-btn-primary" : "ti-btn-light"}`}
                onClick={() => {
                  setBack(choice.back);
                  setOpen(undefined);
                }}
              >
                {choice.label}
              </button>
            ))}
            <select
              aria-label="Earlier day"
              className={`form-control !w-auto !py-1 ${back > 1 ? "!border-primary text-primary" : ""}`}
              value={back > 1 ? String(back) : ""}
              onChange={(event) => {
                if (!event.target.value) return;
                setBack(Number(event.target.value));
                setOpen(undefined);
              }}
            >
              <option value="">Earlier day</option>
              {Array.from({ length: KEPT_DAYS - 1 }, (_, index) => index + 2).map((daysBack) => (
                <option key={daysBack} value={daysBack}>
                  {dayName(daysBack)}
                </option>
              ))}
            </select>
          </div>
          {listed.length === 0 ? (
            <Empty icon="ri-file-list-3-line">
              {back === 0
                ? "No sales have been recorded on this iPad today."
                : back === 1
                  ? "No sales were recorded on this iPad yesterday."
                  : `No sales were recorded on this iPad on ${dayName(back)}.`}
            </Empty>
          ) : (
            <ul className="list-none mb-0">
              {listed.map((sale) => (
                <li key={sale.id} className="py-3 border-b border-defaultborder">
                  <button type="button" className="w-full text-start flex items-center justify-between gap-3" onClick={() => setOpen(open === sale.id ? undefined : sale.id)}>
                    <TwoLine
                      main={sale.reference}
                      sub={`${new Date(sale.soldAt).toLocaleTimeString("en-SG", { hour: "2-digit", minute: "2-digit" })} · ${sale.soldByName} · ${sale.lines.length} ${sale.lines.length === 1 ? "item" : "items"}`}
                    />
                    <div className="text-end">
                      <div className="font-semibold">{formatMoney(sale.totalCents)}</div>
                      {seesProfit && costs.has(sale.id) && sale.status === "paid" ? (
                        <div className={`text-[0.6875rem] ${sale.totalCents - (costs.get(sale.id)?.costCents ?? 0) < 0 ? "text-danger" : "text-[#8c9097] dark:text-white/50"}`}>
                          cost {formatMoney(costs.get(sale.id)?.costCents ?? 0)}, profit {formatMoney(sale.totalCents - (costs.get(sale.id)?.costCents ?? 0))}
                          {costs.get(sale.id)?.costMissing.length ? ` (no cost: ${costs.get(sale.id)?.costMissing.join(", ")})` : ""}
                        </div>
                      ) : null}
                      {flagOf(sale.id)?.status === "allowed" ? (
                        <Badge tone="success">Approved</Badge>
                      ) : flagOf(sale.id)?.status === "open" || (sale.asked && !flagOf(sale.id)) ? (
                        <Badge tone="warning">Approval requested</Badge>
                      ) : null}{" "}
                      {sale.status === "void" ? (
                        <Badge tone="light">Voided</Badge>
                      ) : sale.status === "corrected" ? (
                        <Badge tone="light">Corrected</Badge>
                      ) : sale.status === "unpaid" ? (
                        <Badge tone="warning">Unpaid</Badge>
                      ) : (
                        <Badge tone={sale.method === "cash" ? "success" : "primary"}>{sale.method === "cash" ? "Cash" : "PayNow"}</Badge>
                      )}
                    </div>
                  </button>
                  {sale.note ? (
                    <p className={`text-[0.75rem] mt-2 mb-0 ${sale.refused ? "text-danger" : "text-warning"}`}>{sale.note}</p>
                  ) : null}
                  {open === sale.id ? (
                    <div className="mt-3 p-3 rounded-md bg-light">
                      <ul className="list-none mb-3 text-[0.813rem]">
                        {sale.lines.map((line) => (
                          <li key={line.lineId} className="flex justify-between gap-3">
                            <span>
                              {line.quantity} x {line.description}
                            </span>
                            <span>{formatMoney(line.quantity * line.unitPriceCents)}</span>
                          </li>
                        ))}
                      </ul>
                      {/* What was photographed at the table when it was sold (D, 9 October 2026). */}
                      <div className="mb-3">
                        <BoothPhotos
                          subjectId={sale.id}
                          none="No photo was taken with this sale."
                          fromServer={async () => (await api.salePhotos(sale.reference)).photoIds}
                        />
                      </div>
                      {sale.status === "unpaid" ? (
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            className="ti-btn ti-btn-success !mb-0"
                            disabled={busy || !tillAccount(catalog, "cash")}
                            onClick={() => void act(() => payLater(sale, "cash", tillAccount(catalog, "cash"), seller.id))}
                          >
                            Record cash payment
                          </button>
                          <button
                            type="button"
                            className="ti-btn ti-btn-primary !mb-0"
                            disabled={busy || !tillAccount(catalog, "paynow")}
                            onClick={() => void act(() => payLater(sale, "paynow", tillAccount(catalog, "paynow"), seller.id))}
                          >
                            Record PayNow payment
                          </button>
                          <span className="self-center text-[0.75rem] text-[#8c9097] dark:text-white/50">
                            PayNow reference <span className="font-semibold text-defaulttextcolor dark:text-white">{codeOf(sale.reference)}</span>
                          </span>
                          {!tillAccount(catalog, "cash") || !tillAccount(catalog, "paynow") ? (
                            <p className="basis-full text-[0.75rem] text-[#8c9097] dark:text-white/50 mb-0">
                              A payment must be recorded against a payment account. A system administrator configures this
                              iPad's payment accounts on the dashboard, under Devices.
                            </p>
                          ) : null}
                        </div>
                      ) : null}
                      {/*
                        A paid sale can be voided at the booth too (D, 4 October): it is taken
                        back, money and stock, with the reason, for a sale keyed wrong that is
                        not to be sold again. Correct this sale is for one that is.
                      */}
                      {sale.status === "unpaid" || (sale.status === "paid" && !sale.refused) ? (
                        <div className="flex gap-2 mt-3">
                          <input
                            id={`void-${sale.id}`}
                            type="text"
                            aria-label="Why it is voided"
                            className="form-control"
                            value={reason}
                            placeholder="Reason for voiding"
                            onChange={(event) => setReason(event.target.value)}
                          />
                          <button
                            type="button"
                            className="ti-btn ti-btn-danger !mb-0 shrink-0"
                            disabled={busy || !reason.trim()}
                            onClick={() =>
                              canVoid ? void act(() => voidSale(sale, reason.trim(), seller.id)) : setApproving(sale)
                            }
                          >
                            {busy ? <Working label="Voiding" /> : canVoid ? "Void sale" : "Void sale - approval required"}
                          </button>
                        </div>
                      ) : null}
                      {(sale.status === "paid" || sale.status === "unpaid") && !sale.refused ? (
                        canVoid ? (
                          <div className="mt-3">
                            <button
                              type="button"
                              className="ti-btn ti-btn-warning !mb-0"
                              disabled={busy}
                              onClick={() => onCorrect(sale, { id: seller.id, name: seller.name })}
                            >
                              <i className="ri-edit-2-line" /> Correct sale
                            </button>
                            <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-2 mb-0">
                              The sale reopens in the basket. Correct it and complete checkout; this sale is then reversed
                              {sale.status === "paid" ? ", payment and inventory," : ""} automatically, recording who corrected it and
                              the replacement sale.
                            </p>
                          </div>
                        ) : (
                          <PutRight
                            sale={sale}
                            flag={flagOf(sale.id)}
                            canVoid={canVoid}
                            busy={busy}
                            onCorrect={onCorrect}
                            onAskPin={() => setCorrecting(sale)}
                            onAsk={(note) => void act(() => flagSale(sale, note, seller.id))}
                          />
                        )
                      ) : null}
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </PanelSection>

        {buybacks.length > 0 ? <BoughtToday buybacks={buybacks} photosLeft={photosLeft} /> : null}
        </>
        )}

      {canSetAccounts ? <DeviceAccounts catalog={catalog} onSaved={onReload} /> : null}
      </Offcanvas>

      {approving ? (
        <PosStaff
          catalog={catalog}
          title="Supervisor approval - void sale"
          onlyVoiders
          onChosen={(approver) => {
            const sale = approving;
            setApproving(undefined);
            void act(() => voidSale(sale, reason.trim(), approver.id));
          }}
          onClose={() => setApproving(undefined)}
        />
      ) : null}

      {correcting ? (
        <PosStaff
          catalog={catalog}
          title="Supervisor approval - correct sale"
          onlyVoiders
          onChosen={(approver) => {
            const sale = correcting;
            setCorrecting(undefined);
            onCorrect(sale, { id: approver.id, name: approver.name });
          }}
          onClose={() => setCorrecting(undefined)}
        />
      ) : null}
    </>
  );
}

/**
 * Putting a sale right when the person at the iPad cannot allow it themselves.
 *
 * A supervisor at the booth enters their PIN, as before. One who is not there is asked:
 * the question waits on the desk, where they allow or refuse it from wherever they are,
 * and once it is allowed the correction is finished here without a PIN, in their name.
 */
function PutRight({
  sale,
  flag,
  canVoid,
  busy,
  onCorrect,
  onAskPin,
  onAsk,
}: {
  readonly sale: LocalSale;
  readonly flag: SaleFlagState | undefined;
  readonly canVoid: boolean;
  readonly busy: boolean;
  readonly onCorrect: (sale: LocalSale, by: { id: string; name: string }) => void;
  readonly onAskPin: () => void;
  readonly onAsk: (note: string) => void;
}): React.ReactElement | null {
  const [asking, setAsking] = useState(false);
  const [note, setNote] = useState("");
  if (canVoid) return null;

  if (flag?.status === "allowed" && flag.decidedBy) {
    const by = flag.decidedBy;
    return (
      <div className="mt-3">
        <p className="text-[0.813rem] text-success mb-2">
          {by.name} approved the correction{flag.decisionNote ? `: ${flag.decisionNote}` : "."} Select Apply approved
          correction: the sale reopens in the basket to be corrected and completed, and this sale is reversed.
        </p>
        <button type="button" className="ti-btn ti-btn-warning !mb-0" disabled={busy} onClick={() => onCorrect(sale, by)}>
          <i className="ri-edit-2-line" /> Apply approved correction
        </button>
      </div>
    );
  }

  const waiting = flag?.status === "open" || (!flag && Boolean(sale.asked));
  return (
    <div className="mt-3">
      {waiting ? (
        <p className="text-[0.813rem] text-warning mb-2">
          Supervisor approval requested: {flag?.note ?? sale.asked}. Once it is approved on the dashboard, the correction
          can be completed here.
        </p>
      ) : null}
      {flag?.status === "refused" ? (
        <p className="text-[0.813rem] text-danger mb-2">
          {flag.decidedBy?.name ?? "A supervisor"} rejected the request{flag.decisionNote ? `: ${flag.decisionNote}` : "."}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <button type="button" className="ti-btn ti-btn-warning !mb-0" disabled={busy} onClick={onAskPin}>
          <i className="ri-edit-2-line" /> Correct sale - approval required
        </button>
        {!waiting ? (
          <button type="button" className="ti-btn ti-btn-light !mb-0" disabled={busy} onClick={() => setAsking(true)}>
            <i className="ri-question-line" /> Request supervisor approval
          </button>
        ) : null}
      </div>
      {asking && !waiting ? (
        <div className="flex gap-2 mt-2">
          <input
            id={`ask-${sale.id}`}
            type="text"
            aria-label="What is wrong"
            className="form-control"
            maxLength={300}
            value={note}
            placeholder="Describe the issue, for example one pack too many or wrong price"
            onChange={(event) => setNote(event.target.value)}
          />
          <button
            type="button"
            className="ti-btn ti-btn-primary-full !mb-0 shrink-0"
            disabled={busy || !note.trim()}
            onClick={() => {
              onAsk(note);
              setAsking(false);
              setNote("");
            }}
          >
            Send request
          </button>
        </div>
      ) : null}
      <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-2 mb-0">
        If a supervisor is present, they approve with their password. Otherwise, request supervisor approval; they can
        respond from the dashboard wherever they are.
      </p>
    </div>
  );
}

/** What was bought at the booth today, and whether its photos have gone up yet. */
function BoughtToday({
  buybacks,
  photosLeft,
}: {
  readonly buybacks: readonly LocalBuyback[];
  readonly photosLeft: number;
}): React.ReactElement {
  const total = buybacks.reduce((sum, buyback) => sum + buyback.amountCents, 0);
  const [open, setOpen] = useState<LocalBuyback | undefined>();
  return (
    <PanelSection title="Customer buybacks today">
      {open ? <BuybackPeek buyback={open} onClose={() => setOpen(undefined)} /> : null}
      <div className="flex items-baseline justify-between mb-3">
        <span className="text-[#8c9097] dark:text-white/50 text-[0.75rem]">
          {buybacks.length} {buybacks.length === 1 ? "buyback" : "buybacks"}
          {photosLeft > 0 ? `, ${photosLeft} ${photosLeft === 1 ? "photo" : "photos"} waiting to upload` : ""}
        </span>
        <span className="font-semibold text-[1.125rem]">{formatMoney(total)}</span>
      </div>
      <ul className="list-none m-0 p-0 divide-y divide-defaultborder dark:divide-defaultborder/10">
        {buybacks.map((buyback) => (
          <li key={buyback.id} className="py-3 cursor-pointer" onClick={() => setOpen(buyback)}>
            <div className="flex items-start justify-between gap-3">
              <TwoLine
                main={
                  <span className="flex items-center gap-2">
                    {buyback.reference}
                    <i className="ri-image-line text-[#8c9097] dark:text-white/50" />
                    {buyback.refused ? <Badge tone="danger">Rejected</Badge> : null}
                  </span>
                }
                sub={`${buyback.sellerName} · ${buyback.description}`}
              />
              <div className="text-end shrink-0">
                <div className="font-semibold">{formatMoney(buyback.amountCents)}</div>
                <div className="text-[0.6875rem] text-[#8c9097] dark:text-white/50">
                  <span className={buyback.method === "later" ? "text-warning" : buyback.method === "cash" ? "text-success" : "text-primary"}>
                    {buyback.method === "later" ? "Unpaid" : buyback.method === "cash" ? "Cash" : "PayNow"}
                  </span>
                  {buyback.photos > 0 ? ` · ${buyback.photos} ${buyback.photos === 1 ? "photo" : "photos"}` : ""}
                </div>
              </div>
            </div>
            {buyback.note ? (
              <p className="text-[0.75rem] text-danger mt-1 mb-0">{buyback.note}</p>
            ) : null}
          </li>
        ))}
      </ul>
    </PanelSection>
  );
}

/**
 * One buyback, tapped on the list: what was bought, from whom, and its photos (D, 9 October
 * 2026: "tap on the buyback and let me see what is the card"). Photos not yet uploaded are
 * shown from the iPad; the rest come from the server when there is a connection.
 */
function BuybackPeek({ buyback, onClose }: { readonly buyback: LocalBuyback; readonly onClose: () => void }): React.ReactElement {
  return (
    <Modal open size="lg" title={`Buyback ${buyback.reference}`} onClose={onClose}>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-[0.875rem] mb-4">
        <div>
          <div className="text-[#8c9097] dark:text-white/50 text-[0.75rem]">Item</div>
          <div className="font-semibold">{buyback.description}</div>
        </div>
        <div className="text-end">
          <div className="text-[#8c9097] dark:text-white/50 text-[0.75rem]">Paid</div>
          <div className="font-semibold">
            {formatMoney(buyback.amountCents)} {buyback.method === "later" ? "(unpaid)" : buyback.method === "cash" ? "cash" : "PayNow"}
          </div>
        </div>
        <div>
          <div className="text-[#8c9097] dark:text-white/50 text-[0.75rem]">Seller</div>
          <div>
            {buyback.sellerName}
            {buyback.sellerPhone ? ` · ${buyback.sellerPhone}` : ""}
          </div>
        </div>
        <div className="text-end">
          <div className="text-[#8c9097] dark:text-white/50 text-[0.75rem]">Bought by</div>
          <div>
            {buyback.boughtByName},{" "}
            {new Date(buyback.boughtAt).toLocaleTimeString("en-SG", { hour: "numeric", minute: "2-digit", timeZone: "Asia/Singapore" })}
          </div>
        </div>
      </div>
      <BoothPhotos
        subjectId={buyback.id}
        none="No photo was taken of this buyback."
        fromServer={async () => {
          const { buybacks } = await api.buybacks({ day: buyback.boughtAt.slice(0, 10) });
          const found = buybacks.find((one) => one.id === buyback.id || one.reference === buyback.reference);
          return found?.photoIds ?? [];
        }}
      />
    </Modal>
  );
}

/**
 * The photos of a buyback or a sale: those already on the server, and those still on this
 * iPad waiting to upload. Tap one to see it full screen.
 */
function BoothPhotos({
  subjectId,
  none,
  fromServer,
}: {
  readonly subjectId: string;
  readonly none: string;
  readonly fromServer: () => Promise<readonly string[]>;
}): React.ReactElement {
  const [photos, setPhotos] = useState<string[] | undefined>();
  const [offline, setOffline] = useState(false);
  const [big, setBig] = useState<string | undefined>();

  useEffect(() => {
    let gone = false;
    void (async () => {
      const here = await photosOnDevice(subjectId);
      let there: string[] = [];
      try {
        there = (await fromServer()).map((id) => `/api/photos/${id}`);
      } catch (problem) {
        // A sale not on the server yet answers 404; that is not being offline.
        if (!gone && !(problem instanceof ApiError)) setOffline(true);
      }
      if (!gone) setPhotos([...there, ...here]);
    })();
    return () => {
      gone = true;
    };
  }, [subjectId]);

  if (photos === undefined) return <Working label="Loading photos" />;
  return (
    <>
      {photos.length === 0 ? (
        <p className="text-[#8c9097] dark:text-white/50 text-[0.813rem] mb-0">
          {offline ? "No connection, so only photos still on this iPad can be shown, and there are none." : none}
        </p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {photos.map((src) => (
            <button key={src.slice(-40)} type="button" className="p-0 border-0 bg-transparent" onClick={() => setBig(src)}>
              <img src={src} alt="" className="w-full aspect-square object-cover rounded-md border border-defaultborder" />
            </button>
          ))}
        </div>
      )}
      {offline && photos.length > 0 ? (
        <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-2 mb-0">No connection: only the photos still on this iPad are shown.</p>
      ) : null}
      {big ? (
        <div className="fixed inset-0 z-[300] bg-black/85 flex items-center justify-center p-4" onClick={() => setBig(undefined)}>
          <img src={big} alt="" className="max-w-full max-h-full object-contain rounded-md" />
        </div>
      ) : null}
    </>
  );
}

/** The short code typed into OneCollect, or the reference itself if it is not one of ours. */
function codeOf(reference: string): string {
  try {
    return paynowCode(reference);
  } catch {
    return reference;
  }
}

/** Where this iPad's money goes by default. Needs a connection, and an admin. */
function DeviceAccounts({
  catalog,
  onSaved,
}: {
  readonly catalog: Catalog;
  readonly onSaved: () => Promise<Catalog | undefined>;
}): React.ReactElement {
  const [cashId, setCashId] = useState(catalog.device.cashAccountId ?? "");
  const [paynowId, setPaynowId] = useState(catalog.device.paynowAccountId ?? "");
  const [message, setMessage] = useState<{ tone: "success" | "danger"; text: string } | undefined>();
  const [busy, setBusy] = useState(false);

  async function save(): Promise<void> {
    setBusy(true);
    setMessage(undefined);
    try {
      const response = await fetch("/api/pos/device-accounts", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cashAccountId: cashId || null, paynowAccountId: paynowId || null }),
      });
      const body = (await response.json().catch(() => ({}))) as { error?: string };
      if (!response.ok) {
        setMessage({ tone: "danger", text: body.error ?? "Not saved" });
        return;
      }
      // Fetched again now, so the till uses them from the next sale without being reopened.
      await onSaved();
      setMessage({ tone: "success", text: "Saved. The next sale uses these accounts." });
    } catch {
      setMessage({ tone: "danger", text: "This requires an internet connection. Try again when the iPad is online" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <PanelSection title="Configure payment accounts" last>
      {message ? <Alert tone={message.tone}>{message.text}</Alert> : null}
      <div className="grid grid-cols-12 gap-4">
        <div className="sm:col-span-6 col-span-12">
          <label htmlFor="device-cash" className="form-label">
            Cash account
          </label>
          <SelectField
            id="device-cash"
            value={cashId}
            placeholder="Select account"
            options={catalog.accounts.filter((account) => account.kind === "cash").map((account) => ({ value: account.id, label: account.name }))}
            onChange={setCashId}
          />
        </div>
        <div className="sm:col-span-6 col-span-12">
          <label htmlFor="device-paynow" className="form-label">
            PayNow account
          </label>
          <SelectField
            id="device-paynow"
            value={paynowId}
            placeholder="Select account"
            options={catalog.accounts.filter((account) => account.kind === "bank").map((account) => ({ value: account.id, label: account.name }))}
            onChange={setPaynowId}
          />
        </div>
      </div>
      <button type="button" className="ti-btn ti-btn-primary-full mt-4 !mb-0" disabled={busy} onClick={() => void save()}>
        {busy ? <Working label="Saving" /> : "Save"}
      </button>
    </PanelSection>
  );
}
